import React, { useState, useEffect, useCallback } from 'react';
import AdminNav from './AdminNav';
import './Manager.css';
import './PaymentsManager.css';
import { API_BASE } from '../config/api';

const STATUS_COLORS = {
  pending:   { bg: '#fff8e1', color: '#f57f17', label: 'Pending' },
  confirmed: { bg: '#e8f5e9', color: '#2e7d32', label: 'Confirmed' },
  rejected:  { bg: '#ffebee', color: '#c62828', label: 'Rejected' },
  expired:   { bg: '#f5f5f5', color: '#757575', label: 'Expired' },
};

function badge(status) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span className="pm-badge" style={{ background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

function formatDhaka(dateStr) {
  return new Date(dateStr).toLocaleString('en-US', {
    timeZone: 'Asia/Dhaka',
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

export default function PaymentsManager() {
  const [payments, setPayments]       = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading]         = useState(false);
  const [selected, setSelected]       = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const token = () => localStorage.getItem('adminToken');

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const url = statusFilter
        ? `${API_BASE}/bkash/payments?status=${statusFilter}&limit=100`
        : `${API_BASE}/bkash/payments?limit=100`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token()}` } });
      const data = await res.json();
      if (data.success) {
        setPayments(data.payments);
        setPendingCount(data.pendingCount);
      }
    } catch (e) {
      console.error('Failed to fetch payments', e);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const confirm = async (id) => {
    if (!window.confirm('Confirm this payment and fulfill the order?')) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/bkash/payments/${id}/confirm`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token()}` }
      });
      const data = await res.json();
      if (data.success) { setSelected(null); fetchPayments(); }
      else alert(data.message || 'Failed to confirm');
    } catch { alert('Network error'); }
    finally { setActionLoading(false); }
  };

  const reject = async (id) => {
    if (!window.confirm('Reject this payment?')) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/bkash/payments/${id}/reject`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectReason || 'Transaction could not be verified' })
      });
      const data = await res.json();
      if (data.success) { setSelected(null); setRejectReason(''); fetchPayments(); }
      else alert(data.message || 'Failed to reject');
    } catch { alert('Network error'); }
    finally { setActionLoading(false); }
  };

  return (
    <div className="manager-layout">
      <AdminNav pendingPayments={pendingCount} />

      <div className="manager-content">
        <div className="manager-header">
          <div className="manager-title-row">
            <h1 className="manager-title">
              bKash Payments
              {pendingCount > 0 && (
                <span className="pm-pending-badge">{pendingCount} pending</span>
              )}
            </h1>
          </div>

          <div className="pm-filter-row">
            {['pending', 'confirmed', 'rejected', 'expired', ''].map((s) => (
              <button
                key={s}
                className={`pm-filter-btn ${statusFilter === s ? 'active' : ''}`}
                onClick={() => { setStatusFilter(s); setSelected(null); }}
              >
                {s === '' ? 'All' : STATUS_COLORS[s]?.label || s}
              </button>
            ))}
          </div>
        </div>

        <div className="pm-body">
          {/* ── Table ── */}
          <div className={`pm-table-wrap ${selected ? 'pm-split' : ''}`}>
            {loading ? (
              <p className="pm-loading">Loading…</p>
            ) : payments.length === 0 ? (
              <p className="pm-empty">No payments found.</p>
            ) : (
              <table className="pm-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Customer</th>
                    <th>Sender bKash</th>
                    <th>TrxID</th>
                    <th>Amount</th>
                    <th>Submitted</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr
                      key={p._id}
                      className={selected?._id === p._id ? 'pm-row-active' : ''}
                      onClick={() => { setSelected(p); setRejectReason(''); }}
                    >
                      <td className="pm-order-num">{p.orderNumber}</td>
                      <td>
                        <div className="pm-customer-name">{p.customerName}</div>
                        <div className="pm-customer-phone">{p.customerPhone}</div>
                      </td>
                      <td>{p.senderBkashNumber}</td>
                      <td><code className="pm-trxid">{p.trxId}</code></td>
                      <td className="pm-amount">৳{p.amount.toFixed(2)}</td>
                      <td className="pm-date">{formatDhaka(p.createdAt)}</td>
                      <td>{badge(p.status)}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {p.status === 'pending' && (
                          <div className="pm-action-btns">
                            <button
                              className="pm-btn pm-btn-confirm"
                              onClick={() => confirm(p._id)}
                              disabled={actionLoading}
                            >
                              Confirm
                            </button>
                            <button
                              className="pm-btn pm-btn-reject"
                              onClick={() => { setSelected(p); setRejectReason(''); }}
                              disabled={actionLoading}
                            >
                              Reject
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Detail panel ── */}
          {selected && (
            <div className="pm-detail">
              <div className="pm-detail-header">
                <h3>Payment Detail</h3>
                <button className="pm-detail-close" onClick={() => setSelected(null)}>✕</button>
              </div>

              <div className="pm-detail-body">
                <div className="pm-detail-row"><span>Order #</span><strong>{selected.orderNumber}</strong></div>
                <div className="pm-detail-row"><span>Status</span>{badge(selected.status)}</div>
                <div className="pm-detail-row"><span>Amount</span><strong className="pm-amount">৳{selected.amount.toFixed(2)}</strong></div>
                <hr className="pm-divider" />
                <div className="pm-detail-row"><span>Customer</span><strong>{selected.customerName}</strong></div>
                <div className="pm-detail-row"><span>Phone</span>{selected.customerPhone}</div>
                {selected.customerEmail && (
                  <div className="pm-detail-row"><span>Email</span>{selected.customerEmail}</div>
                )}
                <hr className="pm-divider" />
                <div className="pm-detail-row"><span>Sender bKash</span><strong>{selected.senderBkashNumber}</strong></div>
                <div className="pm-detail-row"><span>TrxID</span><code className="pm-trxid">{selected.trxId}</code></div>
                <div className="pm-detail-row"><span>Submitted</span>{formatDhaka(selected.createdAt)}</div>
                {selected.verifiedAt && (
                  <div className="pm-detail-row"><span>Verified at</span>{formatDhaka(selected.verifiedAt)}</div>
                )}
                {selected.verifiedBy && (
                  <div className="pm-detail-row"><span>Verified by</span>{selected.verifiedBy}</div>
                )}
                {selected.rejectionReason && (
                  <div className="pm-detail-row"><span>Reason</span>{selected.rejectionReason}</div>
                )}
              </div>

              {selected.status === 'pending' && (
                <div className="pm-detail-actions">
                  <button
                    className="pm-btn pm-btn-confirm pm-btn-full"
                    onClick={() => confirm(selected._id)}
                    disabled={actionLoading}
                  >
                    ✓ Confirm Payment
                  </button>
                  <div className="pm-reject-section">
                    <input
                      type="text"
                      placeholder="Rejection reason (optional)"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      className="pm-reason-input"
                    />
                    <button
                      className="pm-btn pm-btn-reject pm-btn-full"
                      onClick={() => reject(selected._id)}
                      disabled={actionLoading}
                    >
                      ✕ Reject Payment
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
